/**
 * AI Copilot Service — Zenith Digital CRP Universal Agent Dvigateli
 * Barcha ERP harakatlari (Sotuv, Vitrina, Mahsulot, Kategoriya, Birlik, Mijoz, Kassa, Hisobotlar) ni to'liq bajaradi.
 */
import { request } from "../../utils/config";
import i18n from "../../services/i18n"
import React from "react";
import { message, Modal } from "antd";
import { currentUiLang, langInfo } from "../../utils/uiLanguages";

export const GEMINI_FAST_MODEL = "gemini-3.5-flash-lite";

/**
 * AI so'rovi backend proksi orqali ketadi. Ilgari kalit shu faylda turardi
 * va build ichida har bir brauzerga yuklanardi — sahifani ochgan har kim uni
 * ko'ra olardi. Endi kalit faqat serverda, brauzer esa Ai/Chat ga murojaat
 * qiladi. Javob Gemini'dan kelgan holicha qaytadi, shuning uchun pastdagi
 * javobni ajratish mantig'i o'zgarmadi.
 */
export async function callGemini(
  payload: any,
  opts?: { silent?: boolean }
): Promise<any> {
  const response = await request({
    url: "Ai/Chat",
    method: "POST",
    data: { ...payload, model: GEMINI_FAST_MODEL },
    silent: opts?.silent,
    headers: {
      Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
    },
  });
  return response.data;
}

export interface SaleActionDraft {
  type: "sale";
  customerName: string;
  productName: string;
  amount: number;
  priceUSD: number;
  totalPriceUSD: number;
  cashUSD: number;
  debtUSD: number;
  notes?: string;
}

export interface ShowcaseActionDraft {
  type: "showcase";
  customerName: string;
  productName: string;
  count: number;
  notes?: string;
}

export interface ProductActionDraft {
  type: "product";
  article: string;
  nameModel: string;
  priceUSD: number;
  categoryId?: number;
  unitId?: number;
}

export interface CategoryActionDraft {
  type: "category";
  name: string;
}

export interface UnitActionDraft {
  type: "unit";
  name: string;
}

export interface CounterpartyActionDraft {
  type: "counterparty";
  name: string;
  phone?: string;
  address?: string;
}

export interface MoneyActionDraft {
  type: "money";
  priceUSD: number;
  transactionType: "income" | "expense";
  comment?: string;
}

export interface ReportData {
  title: string;
  headers: string[];
  rows: Array<Array<string | number>>;
}

export type ActionDraft =
  | SaleActionDraft
  | ShowcaseActionDraft
  | ProductActionDraft
  | CategoryActionDraft
  | UnitActionDraft
  | CounterpartyActionDraft
  | MoneyActionDraft;

export interface AIChatMessage {
  id: string;
  sender: "user" | "ai" | "system";
  text: string;
  timestamp: Date;
  isStreaming?: boolean;
  actionDraft?: ActionDraft;
  reportData?: ReportData;
  actions?: Array<{
    label: string;
    actionType: "navigate" | "excel" | "copy";
    payload: string;
  }>;
}

// In-Memory Kesh (0ms kechikish uchun)
let cachedERPContext: any = null;
let lastCacheTime = 0;
const CACHE_TTL_MS = 60 * 1000; // 1 daqiqa

/**
 * Foydalanuvchiga ruxsat etilgan bo'limlar (AccountAPI/Me dan keladi).
 * Super-admin uchun null qaytadi — unga hamma narsa ochiq.
 * Ro'yxat bo'sh yoki o'qib bo'lmasa ham null qaytadi: bunday holda
 * eski xatti-harakat saqlanadi, xodim AI dan bexosdan uzilib qolmaydi.
 */
export function allowedAiModules(): Set<string> | null {
  try {
    if (localStorage.getItem("is_super_admin") === "1") return null;
    const raw = localStorage.getItem("allowed_modules");
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed) || parsed.length === 0) return null;
    return new Set<string>(parsed);
  } catch {
    return null;
  }
}

/** Bo'lim shu foydalanuvchiga ochiqmi */
export function canUseModule(code: string): boolean {
  const allowed = allowedAiModules();
  return allowed === null || allowed.has(code);
}

/**
 * ERP ma'lumotlarini orqa fonda yuklash
 */
export async function fetchERPContext(forceRefresh = false) {
  const now = Date.now();
  if (!forceRefresh && cachedERPContext && now - lastCacheTime < CACHE_TTL_MS) {
    return cachedERPContext;
  }

  try {
    // Har bir so'rov o'ziga tegishli bo'lim ochiq bo'lgandagina yuboriladi:
    // menejer AI orqali o'ziga biriktirilmagan bo'lim ma'lumotini ko'rmasin
    const ask = (code: string, opts: any) =>
      canUseModule(code) ? request({ ...opts, silent: true }) : Promise.resolve(null);

    const [
      statsRes,
      salesRes,
      residuesRes,
      moneysRes,
      counterpartiesRes,
      warehousesRes,
      paymentTypesRes,
      productsRes,
      categoriesRes,
    ] = await Promise.allSettled([
      ask("home", { url: "Reports/HeaderProductCounterpartyCount" }),
      ask("sales", { url: "Sales", params: { PageNumber: 1, PageSize: 25 } }),
      ask("residues", { url: "Residues", params: { PageNumber: 1, PageSize: 50 } }),
      ask("moneys", { url: "Moneys", params: { PageNumber: 1, PageSize: 20 } }),
      ask("counterparties", { url: "Counterparties", params: { PageNumber: 1, PageSize: 50 } }),
      ask("warehouses", { url: "Warehouses", params: { PageNumber: 1, PageSize: 20 } }),
      ask("paymentTypes", { url: "MoneyPaymentType", params: { PageNumber: 1, PageSize: 20 } }),
      ask("products", { url: "Products", params: { PageNumber: 1, PageSize: 50 } }),
      ask("productCategories", { url: "productCategory", params: { PageNumber: 1, PageSize: 50 } }),
    ]);

    const stats = statsRes.status === "fulfilled" ? statsRes.value?.data || statsRes.value : {};
    const sales = salesRes.status === "fulfilled" ? salesRes.value?.data?.data || salesRes.value?.data || [] : [];
    const residues = residuesRes.status === "fulfilled" ? residuesRes.value?.data?.data || residuesRes.value?.data || [] : [];
    const moneys = moneysRes.status === "fulfilled" ? moneysRes.value?.data?.data || moneysRes.value?.data || [] : [];
    const counterparties = counterpartiesRes.status === "fulfilled" ? counterpartiesRes.value?.data?.data || counterpartiesRes.value?.data || [] : [];
    const warehouses = warehousesRes.status === "fulfilled" ? warehousesRes.value?.data?.data || warehousesRes.value?.data || [] : [];
    const paymentTypes = paymentTypesRes.status === "fulfilled" ? paymentTypesRes.value?.data?.data || paymentTypesRes.value?.data || [] : [];
    const products = productsRes.status === "fulfilled" ? productsRes.value?.data?.data || productsRes.value?.data || [] : [];
    const categories = categoriesRes.status === "fulfilled" ? categoriesRes.value?.data?.data || categoriesRes.value?.data || [] : [];

    cachedERPContext = { stats, sales, residues, moneys, counterparties, warehouses, paymentTypes, products, categories };
    lastCacheTime = now;
    return cachedERPContext;
  } catch (err) {
    console.warn("AI context background fetch error:", err);
    return cachedERPContext || { stats: {}, sales: [], residues: [], moneys: [], counterparties: [], warehouses: [], paymentTypes: [], products: [], categories: [] };
  }
}

/*
  AI amali uchun tovar va mijozni ANIQ topish.

  2026-10-08 gacha bu yerda xavfli "zaxira" qiymatlar bor edi: tovar topilmasa
  ro'yxatdagi BIRINCHI tovar (yoki id=1, $10) olinardi, mijoz topilmasa birinchi
  mijoz. Ustiga `q.includes(p.article || "")` — artikuli bo'sh tovar HAR QANDAY
  so'zga mos kelardi. Qidiruv faqat birinchi 50 ta tovar ichida bo'lardi.
  Mahsulot modelida `priceUSD` umuman yo'q (CostPriceUSD / SalePriceUSD), shuning
  uchun narx aytilmasa HAR BIR AI sotuvi $10 bo'lib yozilardi. Tovar esa
  foydalanuvchi tasdiqlagandan KEYIN qidirilardi — u nima yozilishini ko'rmasdi.

  Endi: qidiruv backendda, bo'sh qiymat hech narsaga mos kelmaydi, aniq topilmasa
  yoki bir nechtasi mos kelsa amal TO'XTAYDI, narx to'qib chiqarilmaydi va yozishdan
  OLDIN topilgan tovar, narx va mijoz bilan tasdiq so'raladi. Mobil ilovadagi
  aiActions.ts allaqachon shunday ishlaydi.
*/
function aiErr(text: string): Error {
  const e: any = new Error(text)
  e.aiResolve = true
  return e
}

function normText(s: any): string {
  return String(s || "").toLowerCase().replace(/\s+/g, " ").trim()
}

function listFrom(res: any): any[] {
  const d = res?.data
  if (Array.isArray(d)) return d
  if (Array.isArray(d?.data)) return d.data
  return []
}

function uniqById(rows: any[]): any[] {
  const out: any[] = []
  rows.forEach((r) => {
    if (r && r.id && !out.some((u) => u.id === r.id)) out.push(r)
  })
  return out
}

async function searchRows(url: string, text: string): Promise<any[]> {
  try {
    const res: any = await request({ url, params: { PageNumber: 1, PageSize: 20, SearchText: text } })
    return listFrom(res)
  } catch (e) {
    return []
  }
}

type ResolvedProduct = { id: number; price: number; name: string; article: string }

export async function findProductStrict(nameOrArticle: string): Promise<ResolvedProduct> {
  const q = normText(nameOrArticle)
  if (!q) throw aiErr(String(i18n.t("aiProductNotFound", { name: nameOrArticle || "—" })))
  const rows = await searchRows("Products", String(nameOrArticle).trim())
  const nm = (p: any) => normText(p.nameModel)
  const ar = (p: any) => normText(p.article)
  // 1) aniq moslik: artikul yoki nom
  let cands = rows.filter((p) => (ar(p) !== "" && ar(p) === q) || (nm(p) !== "" && nm(p) === q))
  // 2) qism moslik — faqat kamida 3 belgili qiymatlar bilan, bo'sh satr hech narsaga mos kelmaydi
  if (cands.length === 0 && q.length >= 3) {
    cands = rows.filter(
      (p) =>
        (nm(p).length >= 3 && (nm(p).includes(q) || q.includes(nm(p)))) ||
        (ar(p).length >= 3 && (ar(p).includes(q) || q.includes(ar(p))))
    )
  }
  const uniq = uniqById(cands)
  if (uniq.length === 0) throw aiErr(String(i18n.t("aiProductNotFound", { name: nameOrArticle })))
  if (uniq.length > 1) {
    const list = uniq.slice(0, 3).map((p) => p.nameModel || p.article).join(", ")
    throw aiErr(String(i18n.t("aiProductAmbiguous", { list })))
  }
  const p = uniq[0]
  return {
    id: p.id,
    price: Number(p.salePriceUSD ?? 0) || 0,
    name: p.nameModel || p.article || String(p.id),
    article: p.article || "",
  }
}

type ResolvedCounterparty = { id: number | null; name: string; willCreate: boolean }

export async function findCounterparty(nameRaw: string): Promise<ResolvedCounterparty> {
  const q = normText(nameRaw)
  if (!q) throw aiErr(String(i18n.t("aiCustomerEmpty")))
  const fullOf = (c: any) => normText([c.name, c.lastname].filter(Boolean).join(" "))
  const shown = (c: any) => [c.name, c.lastname].filter(Boolean).join(" ")
  let rows = await searchRows("Counterparties", String(nameRaw).trim())
  // "Ali Valiyev" — ism va familiya alohida maydonda: butun matn bilan topilmasa
  // birinchi so'z bilan qidirib, TO'LIQ ismni aniq solishtiramiz (takroriy
  // mijoz yaratilmasligi uchun)
  if (rows.length === 0 && q.indexOf(" ") > 0) {
    const first = q.split(" ")[0]
    if (first.length >= 3) rows = await searchRows("Counterparties", first)
  }
  let cands = rows.filter((c) => (normText(c.name) !== "" && normText(c.name) === q) || (fullOf(c) !== "" && fullOf(c) === q))
  if (cands.length === 0 && q.length >= 3) {
    cands = rows.filter((c) => fullOf(c).length >= 3 && fullOf(c).includes(q))
  }
  const uniq = uniqById(cands)
  if (uniq.length > 1) {
    throw aiErr(String(i18n.t("aiCustomerAmbiguous", { list: uniq.slice(0, 3).map(shown).join(", ") })))
  }
  if (uniq.length === 1) return { id: uniq[0].id, name: shown(uniq[0]), willCreate: false }
  return { id: null, name: String(nameRaw).trim(), willCreate: true }
}

async function createCounterparty(name: string): Promise<number> {
  let id: any = null
  try {
    const res: any = await request({ url: "Counterparties", method: "POST", data: { name } })
    id = res?.data?.id || res?.data?.data?.id
  } catch (e) {
    id = null
  }
  if (!id) throw aiErr(String(i18n.t("aiCustomerCreateFailed")))
  return id
}

/** Yozishdan OLDIN topilgan qiymatlar bilan tasdiq */
function confirmResolved(title: string, lines: string[]): Promise<boolean> {
  return new Promise((resolve) => {
    Modal.confirm({
      title,
      content: React.createElement(
        "div",
        null,
        lines.map((l, i) => React.createElement("div", { key: i, style: { marginTop: i ? 4 : 0 } }, l))
      ),
      okText: String(i18n.t("save")),
      cancelText: String(i18n.t("cancel")),
      zIndex: 1500,
      onOk: () => resolve(true),
      onCancel: () => resolve(false),
    })
  })
}

function aiFail(err: any, fallbackKey: string) {
  if (err && err.aiResolve) {
    message.error({ content: err.message, key: "actionExec", duration: 6 })
  } else {
    console.error("AI action error:", err)
    const back = err?.response?.data?.message || err?.response?.data?.error
    message.error({
      content: String(i18n.t(fallbackKey)) + (back ? ": " + String(i18n.t(String(back))) : ""),
      key: "actionExec",
      duration: 5,
    })
  }
}

function resolveWarehouseId(context: any): number {
  return context.warehouses?.[0]?.id || 1;
}

function resolvePaymentTypeId(context: any): number {
  return context.paymentTypes?.[0]?.id || 1;
}

/**
 * Gemini Universal Agent So'rovini Bajarish
 */
export async function streamGeminiChat(
  prompt: string,
  onChunk: (accumulatedText: string) => void,
  imageBase64?: string
): Promise<{ fullText: string; actionDraft?: ActionDraft; reportData?: ReportData }> {
  const context = await fetchERPContext();

  const contextData = {
    tizim: "Zenith Digital CRP",
    bugungiFoydaUSD: context.stats?.todayProfitUSD || 0,
    bugungiTushumUSD: context.stats?.todayIncomeUSD || 0,
    umumiyQarzdorlikUSD: context.stats?.todayDebtUSD || 0,
    qarzdorlarSoni: context.stats?.debtorsCount || 0,
    mijozlarSoni: context.stats?.counterpartyCount || 0,
    xodimlarSoni: context.stats?.managerCount || 0,
    kategoriyalar: (context.categories || []).map((c: any) => ({ id: c.id, nomi: c.name })),
    omborlar: (context.warehouses || []).map((w: any) => ({ id: w.id, nomi: w.name })),
    kassaTurlari: (context.paymentTypes || []).map((p: any) => ({ id: p.id, nomi: p.name })),
    mavjudMijozlar: (context.counterparties || []).slice(0, 15).map((c: any) => ({ id: c.id, nomi: c.name })),
    mavjudTovarlar: (context.products || []).slice(0, 15).map((p: any) => ({ id: p.id, artikul: p.article, nomi: p.nameModel, narxUSD: p.priceUSD })),
    omborQoldiqlari: (context.residues || []).slice(0, 10).map((r: any) => ({
      nomi: r.mainProduct?.nameModel || r.productName,
      artikul: r.mainProduct?.article || r.article,
      qoldiq: r.amount || 0,
      narxUSD: r.mainProduct?.priceUSD || r.priceUSD,
    })),
  };

  // Menejer AI orqali o'ziga biriktirilmagan bo'limga chiqib ketmasligi kerak.
  // Ruxsat ro'yxati kontekstni yig'ishda ham qo'llanadi (yuqoriga qarang),
  // bu yerdagi ko'rsatma esa modelning javobini ham shu doirada ushlab turadi.
  const allowedModules = allowedAiModules();
  const scopeInstruction = allowedModules
    ? `

MUHIM CHEKLOV — sizga faqat quyidagi bo'limlar ochiq:
${Array.from(allowedModules).join(", ")}

Shu ro'yxatdan tashqaridagi bo'lim bo'yicha ma'lumot bermang va amal
bajarmang. Bunday so'rov kelsa qisqa qilib: "Bu bo'lim sizga ochiq emas,
administrator bilan bog'laning" deb javob bering va hech qanday
json-action qaytarmang. Ro'yxatdan tashqaridagi ma'lumotni taxmin qilib
ham aytmang.
`
    : "";

  /*
    Javob tili.

    Standart holat — interfeys tili (ilgari foydalanuvchi ruscha yozsa ham
    javob o'zbekcha kelardi).

    Lekin foydalanuvchi javob tilini aniq aytsa ("ruscha javob ber",
    "answer in English", "o'zbekcha yoz"), o'sha so'rov ustun turadi.
    Ilgari bu yerda "qat'i nazar" deb yozilgani uchun AI foydalanuvchining
    aniq iltimosini ham e'tiborsiz qoldirardi.
  */
  const replyLang = langInfo(currentUiLang()).promptName
  const languageInstruction = `
JAVOB TILI: standart holatda ${replyLang} tilida javob bering.
LEKIN foydalanuvchi javob tilini aniq aytsa (masalan "ruscha javob ber",
"answer in English", "o'zbekcha yoz"), darhol o'sha tilga o'ting va
keyingi javoblarni ham shu tilda davom ettiring — foydalanuvchi boshqa
til so'ramaguncha. Foydalanuvchining aniq so'rovi interfeys tilidan
ustun turadi. Bu jadval sarlavhalari va tugma yozuvlariga ham tegishli.
JSON bloklaridagi kalitlar (type, customerName va h.k.) esa o'zgarmaydi,
ular ingliz tilida qoladi.
`

  const systemInstruction = `Siz "Zenith Digital CRP" ning universal, juda tezkor va barcha amallarni bajara oladigan Avtonom AI Agentisiz.

TAQIQLANGAN TOVARLAR (qat'iy): narkotik, qurol va portlovchi, 18+ xizmat, soxta hujjat yoki pul,
qimor, odam yoki organ savdosi bo'yicha sotuv, tovar yaratish, vitrina yoki hisob-kitob
BAJARILMAYDI. Bunday so'rovga json-action qaytarmang, qisqa javob bering: «Bu tizimda taqiqlangan».
Foydalanuvchi OVOZLI yoki MATNLI har qanday topshiriq berishi mumkin:
${languageInstruction}${scopeInstruction}

1. **ZAKAZ / SOTUV BUYRUG'I:** (masalan: "Aliyevga 30 ta tovar yoz 12$ dan, 100$ naqd, qolgani qarz")
\`\`\`json-action
{
  "type": "sale",
  "customerName": "Aliyev",
  "productName": "1024",
  "amount": 30,
  "priceUSD": 12,
  "totalPriceUSD": 360,
  "cashUSD": 100,
  "debtUSD": 260,
  "notes": "Ovozli buyruq"
}
\`\`\`

2. **VITRINAGA QO'SHISH (SHOWCASE):** (masalan: "Vitrinaga Aliyevga 10 ta 1024 qo'sh")
\`\`\`json-action
{
  "type": "showcase",
  "customerName": "Aliyev",
  "productName": "1024",
  "count": 10,
  "notes": "Vitrinaga topshirildi"
}
\`\`\`

3. **YANGI MAHSULOT QO'SHISH (PRODUCT):** (masalan: "Yangi mahsulot yarat: Artikul A-101, Nomi Krossovka, narxi 20$")
\`\`\`json-action
{
  "type": "product",
  "article": "A-101",
  "nameModel": "Krossovka",
  "priceUSD": 20
}
\`\`\`

4. **KATEGORIYA QO'SHISH (CATEGORY):** (masalan: "Tovar uchun kategoriya qo'sh: Ro'lon" yoki "Yangi kategoriya: Kiyimlar")
\`\`\`json-action
{
  "type": "category",
  "name": "Ro'lon"
}
\`\`\`

5. **O'LCHOV BIRLIGI QO'SHISH (UNIT):** (masalan: "Birlik qo'sh: Metr" yoki "Dona")
\`\`\`json-action
{
  "type": "unit",
  "name": "Metr"
}
\`\`\`

6. **YANGI MIJOZ QO'SHISH (COUNTERPARTY):** (masalan: "Yangi mijoz qo'sh: Bekzod aka, tel +998901234567, Toshkent")
\`\`\`json-action
{
  "type": "counterparty",
  "name": "Bekzod aka",
  "phone": "+998901234567",
  "address": "Toshkent"
}
\`\`\`

7. **KASSA KIRIM / XARAJAT (MONEY):** (masalan: "Kassadan xarajat yoz 50 dollar tushlik uchun" yoki "Kassaga kirim 200$")
\`\`\`json-action
{
  "type": "money",
  "priceUSD": 50,
  "transactionType": "expense",
  "comment": "Tushlik uchun"
}
\`\`\`

8. **HISOBOT / STATISTIKA:** (Savdolar, qoldiqlar, qarzdorlar jadvali)
\`\`\`json-report
{
  "title": "Hisobot nomi",
  "headers": ["Ustun 1", "Ustun 2", "Ustun 3"],
  "rows": [
    ["Qiymat 1", "Qiymat 2", "Qiymat 3"]
  ]
}
\`\`\`

Real ERP Konteksti:
${JSON.stringify(contextData)}`;

  const parts: any[] = [{ text: prompt }];
  if (imageBase64) {
    const cleanBase64 = imageBase64.replace(/^data:image\/[a-z]+;base64,/, "");
    parts.push({
      inlineData: {
        mimeType: "image/jpeg",
        data: cleanBase64,
      },
    });
  }

  const data = await callGemini({
    contents: [{ role: "user", parts }],
    systemInstruction: { parts: [{ text: systemInstruction }] },
    generationConfig: {
      temperature: 0.5,
      maxOutputTokens: 1200,
    },
  });

  const rawText = data?.candidates?.[0]?.content?.parts?.[0]?.text || "";

  // Universal Action JSON ajratish
  let actionDraft: ActionDraft | undefined;
  const jsonActionMatch =
    rawText.match(/```json-action\s*([\s\S]*?)\s*```/) ||
    rawText.match(/```json-order\s*([\s\S]*?)\s*```/) ||
    rawText.match(/```json-showcase\s*([\s\S]*?)\s*```/) ||
    rawText.match(/```json-product\s*([\s\S]*?)\s*```/) ||
    rawText.match(/```json-category\s*([\s\S]*?)\s*```/) ||
    rawText.match(/```json-unit\s*([\s\S]*?)\s*```/) ||
    rawText.match(/```json-counterparty\s*([\s\S]*?)\s*```/) ||
    rawText.match(/```json-money\s*([\s\S]*?)\s*```/);

  if (jsonActionMatch && jsonActionMatch[1]) {
    try {
      const parsed = JSON.parse(jsonActionMatch[1]);
      if (!parsed.type) {
        if (parsed.amount && parsed.customerName) parsed.type = "sale";
        else if (parsed.count && parsed.customerName) parsed.type = "showcase";
        else if (parsed.article || parsed.nameModel) parsed.type = "product";
        else if (parsed.name && (parsed.phone || parsed.address)) parsed.type = "counterparty";
        else if (parsed.priceUSD && parsed.comment) parsed.type = "money";
      }
      actionDraft = parsed;
    } catch (e) {}
  }

  // Report JSON ajratish
  let reportData: ReportData | undefined;
  const jsonReportMatch = rawText.match(/```json-report\s*([\s\S]*?)\s*```/);
  if (jsonReportMatch && jsonReportMatch[1]) {
    try {
      reportData = JSON.parse(jsonReportMatch[1]);
    } catch (e) {}
  }

  // Matnni tozalash
  let cleanDisplay = rawText
    .replace(/```json-(action|order|showcase|product|category|unit|counterparty|money|report)[\s\S]*?```/g, "")
    .trim();

  onChunk(cleanDisplay);

  return { fullText: cleanDisplay, actionDraft, reportData };
}

/**
 * 1. Sotuvni Bazaga Yozish (POST Sales/ArraySale)
 */
export async function executeSaleAction(draft: SaleActionDraft): Promise<boolean> {
  // Bo'lim yopiq bo'lsa amal bajarilmaydi — ko'rsatmaning o'zi yetarli
  // emas, model baribir json-action qaytarib qo'yishi mumkin
  if (!canUseModule("sales")) {
    message.error(i18n.t("msgSectionNotAllowed"));
    return false;
  }
  try {
    message.loading({ content: String(i18n.t("aiChecking")), key: "actionExec" });
    const context = await fetchERPContext();

    // 1) Tovar va mijoz ANIQ topiladi — topilmasa amal to'xtaydi
    const prod = await findProductStrict(draft.productName);
    const cp = await findCounterparty(draft.customerName);

    // 2) Narx: aytilgan bo'lsa o'sha, aks holda tovarning SOTUV narxi.
    //    To'qib chiqarilgan narx ($10) endi yo'q.
    const amount = Number(draft.amount) > 0 ? Number(draft.amount) : 1;
    const price = Number(draft.priceUSD) > 0 ? Number(draft.priceUSD) : prod.price;
    if (!(price > 0)) throw aiErr(String(i18n.t("aiPriceUnknown", { name: prod.name })));
    const totalPrice = Math.round(price * amount * 100) / 100;
    const cash = Math.min(Math.max(Number(draft.cashUSD) || 0, 0), totalPrice);
    message.destroy("actionExec");

    // 3) Yozishdan OLDIN — topilgan haqiqiy qiymatlar bilan tasdiq
    const ok = await confirmResolved(String(i18n.t("aiConfirmSaleTitle")), [
      `${i18n.t("product")}: ${prod.name}${prod.article ? " (" + prod.article + ")" : ""}`,
      `${i18n.t("amount")}: ${amount}`,
      `${i18n.t("price")}: $${price}`,
      `${i18n.t("total")}: $${totalPrice}`,
      `${i18n.t("counterparty")}: ${cp.name}${cp.willCreate ? " — " + i18n.t("aiNewCustomer") : ""}`,
    ]);
    if (!ok) {
      message.info(String(i18n.t("aiSaleCancelled")));
      return false;
    }

    message.loading({ content: String(i18n.t("aiSaving")), key: "actionExec" });
    const counterpartyId = cp.willCreate ? await createCounterparty(cp.name) : (cp.id as number);
    const paymentTypeId = resolvePaymentTypeId(context);

    /*
      Ombor: tovar zaxirasi YETARLI bo'lgani tanlanadi (aks holda backend
      WAREHOUSE_STOCK_NOT_ENOUGH qaytaradi). Qoldiq qatori AYNAN shu tovarniki
      bo'lishi shart — ilgari mos kelmasa birinchi qator (boshqa tovar) olinardi.
    */
    let warehouseId = resolveWarehouseId(context);
    try {
      const res: any = await request({
        url: "Residues",
        params: { PageNumber: 1, PageSize: 10, searchText: prod.article || prod.name },
      });
      const rows: any[] = Array.isArray(res?.data) ? res.data : res?.data?.data || [];
      const row = rows.find((r: any) => (r.id ?? r.productId) === prod.id);
      const stocks: any[] = (row?.stocks || []).filter((s: any) => Number(s.totalCount || 0) >= amount);
      if (stocks.length > 0) {
        stocks.sort((a: any, b: any) => Number(b.totalCount) - Number(a.totalCount));
        warehouseId = stocks[0].warehouseId ?? warehouseId;
      }
    } catch (e) {
      /* qoldiq olinmasa — standart ombor bilan davom etamiz */
    }

    const payload = {
      dateSale: new Date().toISOString(),
      document: `AI-${Date.now().toString().slice(-6)}`,
      warehouseId,
      counterpartyId,
      paymentDate: new Date().toISOString(),
      arrays: [
        {
          productId: prod.id,
          amount,
          priceUSD: price,
          discount: 0,
        },
      ],
      /*
        Backend kamida bitta to'lov qatori talab qiladi (PAYMENT_TYPE_REQUIRED).
        Qolgan summa "Долг" (id 4) qatori bilan yuboriladi, xuddi sotuv formasidagi kabi.
      */
      arrayPaymentType: [
        ...(cash > 0 ? [{ paymentTypeId, paymentPrice: cash }] : []),
        ...(totalPrice - cash > 0
          ? [{ paymentTypeId: 4, paymentPrice: Math.round((totalPrice - cash) * 100) / 100 }]
          : []),
      ],
    };

    await request({
      url: "Sales/ArraySale",
      method: "POST",
      data: payload,
    });

    // Suhbatdagi yakuniy xabar ham HAQIQIY qiymatlarni ko'rsatsin
    draft.customerName = cp.name;
    draft.productName = prod.name;
    draft.totalPriceUSD = totalPrice;

    cachedERPContext = null;
    message.success({
      content: String(i18n.t("aiSaleSaved", { product: prod.name, total: totalPrice })),
      key: "actionExec",
      duration: 3,
    });
    return true;
  } catch (err: any) {
    aiFail(err, "aiSaleFailed");
    return false;
  }
}

/**
 * 2. Vitrinaga Qo'shish (POST Showcases)
 */
export async function executeShowcaseAction(draft: ShowcaseActionDraft): Promise<boolean> {
  // Bo'lim yopiq bo'lsa amal bajarilmaydi — ko'rsatmaning o'zi yetarli
  // emas, model baribir json-action qaytarib qo'yishi mumkin
  if (!canUseModule("showcases")) {
    message.error(i18n.t("msgSectionNotAllowed"));
    return false;
  }
  try {
    message.loading({ content: String(i18n.t("aiChecking")), key: "actionExec" });

    const prod = await findProductStrict(draft.productName);
    const cp = await findCounterparty(draft.customerName);
    const count = Number(draft.count) > 0 ? Number(draft.count) : 1;
    message.destroy("actionExec");

    const ok = await confirmResolved(String(i18n.t("aiConfirmShowcaseTitle")), [
      `${i18n.t("product")}: ${prod.name}${prod.article ? " (" + prod.article + ")" : ""}`,
      `${i18n.t("amount")}: ${count}`,
      `${i18n.t("counterparty")}: ${cp.name}${cp.willCreate ? " — " + i18n.t("aiNewCustomer") : ""}`,
    ]);
    if (!ok) {
      message.info(String(i18n.t("aiSaleCancelled")));
      return false;
    }

    message.loading({ content: String(i18n.t("aiSaving")), key: "actionExec" });
    const counterpartyId = cp.willCreate ? await createCounterparty(cp.name) : (cp.id as number);

    await request({
      url: "Showcases",
      method: "POST",
      data: {
        counterpartyId,
        productId: prod.id,
        count,
        dateShowcase: new Date().toISOString(),
        status: 1,
      },
    });

    draft.customerName = cp.name;
    draft.productName = prod.name;
    draft.count = count;

    cachedERPContext = null;
    message.success({
      content: String(i18n.t("aiShowcaseSaved", { product: prod.name, count })),
      key: "actionExec",
      duration: 3,
    });
    return true;
  } catch (err: any) {
    aiFail(err, "aiShowcaseFailed");
    return false;
  }
}

/**
 * 3. Yangi Mahsulot Qo'shish (POST Products)
 */
export async function executeProductAction(draft: ProductActionDraft): Promise<boolean> {
  // Bo'lim yopiq bo'lsa amal bajarilmaydi — ko'rsatmaning o'zi yetarli
  // emas, model baribir json-action qaytarib qo'yishi mumkin
  if (!canUseModule("products")) {
    message.error(i18n.t("msgSectionNotAllowed"));
    return false;
  }
  try {
    message.loading({ content: "Yangi mahsulot bazaga qo'shilmoqda...", key: "actionExec" });

    await request({
      url: "Products",
      method: "POST",
      data: {
        article: draft.article || `ART-${Date.now().toString().slice(-4)}`,
        nameModel: draft.nameModel || "Yangi Mahsulot",
        priceUSD: draft.priceUSD || 0,
        categoryId: draft.categoryId || 1,
        unitId: draft.unitId || 1,
      },
    });

    cachedERPContext = null;
    message.success({ content: `✅ Yangi mahsulot qo'shildi! (${draft.nameModel} - $${draft.priceUSD})`, key: "actionExec", duration: 3 });
    return true;
  } catch (err: any) {
    console.error("Product execute error:", err);
    message.error({ content: "Xatolik: Mahsulotni qo'shib bo'lmadi", key: "actionExec", duration: 3 });
    return false;
  }
}

/**
 * 4. Yangi Kategoriya Qo'shish (POST productCategory)
 */
export async function executeCategoryAction(draft: CategoryActionDraft): Promise<boolean> {
  // Bo'lim yopiq bo'lsa amal bajarilmaydi — ko'rsatmaning o'zi yetarli
  // emas, model baribir json-action qaytarib qo'yishi mumkin
  if (!canUseModule("productCategories")) {
    message.error(i18n.t("msgSectionNotAllowed"));
    return false;
  }
  try {
    message.loading({ content: "Kategoriya bazaga qo'shilmoqda...", key: "actionExec" });

    await request({
      url: "productCategory",
      method: "POST",
      data: {
        name: draft.name.trim(),
      },
    });

    cachedERPContext = null;
    message.success({ content: `✅ Yangi kategoriya qo'shildi! (${draft.name})`, key: "actionExec", duration: 3 });
    return true;
  } catch (err: any) {
    console.error("Category execute error:", err);
    message.error({ content: "Xatolik: Kategoriyani saqlab bo'lmadi", key: "actionExec", duration: 3 });
    return false;
  }
}

/**
 * 5. Yangi O'lchov Birligi Qo'shish (POST productUnits)
 */
export async function executeUnitAction(draft: UnitActionDraft): Promise<boolean> {
  // Bo'lim yopiq bo'lsa amal bajarilmaydi — ko'rsatmaning o'zi yetarli
  // emas, model baribir json-action qaytarib qo'yishi mumkin
  if (!canUseModule("productUnits")) {
    message.error(i18n.t("msgSectionNotAllowed"));
    return false;
  }
  try {
    message.loading({ content: "O'lchov birligi bazaga qo'shilmoqda...", key: "actionExec" });

    await request({
      url: "productUnits",
      method: "POST",
      data: {
        name: draft.name.trim(),
      },
    });

    cachedERPContext = null;
    message.success({ content: `✅ O'lchov birligi qo'shildi! (${draft.name})`, key: "actionExec", duration: 3 });
    return true;
  } catch (err: any) {
    console.error("Unit execute error:", err);
    message.error({ content: "Xatolik: O'lchov birligini saqlab bo'lmadi", key: "actionExec", duration: 3 });
    return false;
  }
}

/**
 * 6. Yangi Mijoz Qo'shish (POST Counterparties)
 */
export async function executeCounterpartyAction(draft: CounterpartyActionDraft): Promise<boolean> {
  // Bo'lim yopiq bo'lsa amal bajarilmaydi — ko'rsatmaning o'zi yetarli
  // emas, model baribir json-action qaytarib qo'yishi mumkin
  if (!canUseModule("counterparties")) {
    message.error(i18n.t("msgSectionNotAllowed"));
    return false;
  }
  try {
    message.loading({ content: "Yangi mijoz ro'yxatga olinmoqda...", key: "actionExec" });

    await request({
      url: "Counterparties",
      method: "POST",
      data: {
        name: draft.name,
        phone: draft.phone || "",
        address: draft.address || "",
        status: 1,
      },
    });

    cachedERPContext = null;
    message.success({ content: `✅ Mijoz muvaffaqiyatli qo'shildi! (${draft.name})`, key: "actionExec", duration: 3 });
    return true;
  } catch (err: any) {
    console.error("Counterparty execute error:", err);
    message.error({ content: "Xatolik: Mijozni qo'shib bo'lmadi", key: "actionExec", duration: 3 });
    return false;
  }
}

/**
 * 7. Kassa Kirim / Xarajat Kiritish (POST Moneys)
 */
export async function executeMoneyAction(draft: MoneyActionDraft): Promise<boolean> {
  // Bo'lim yopiq bo'lsa amal bajarilmaydi — ko'rsatmaning o'zi yetarli
  // emas, model baribir json-action qaytarib qo'yishi mumkin
  if (!canUseModule("moneys")) {
    message.error(i18n.t("msgSectionNotAllowed"));
    return false;
  }
  try {
    message.loading({ content: "Kassa amali bajarilmoqda...", key: "actionExec" });
    const context = await fetchERPContext();
    const paymentTypeId = resolvePaymentTypeId(context);

    await request({
      url: "Moneys",
      method: "POST",
      data: {
        priceUSD: draft.priceUSD || 0,
        moneyPaymentTypeId: paymentTypeId,
        purposePaymentId: 1,
        comment: draft.comment || (draft.transactionType === "expense" ? "Xarajat" : "Kirim"),
      },
    });

    cachedERPContext = null;
    message.success({ content: `✅ Kassa amali bajarildi! ($${draft.priceUSD})`, key: "actionExec", duration: 3 });
    return true;
  } catch (err: any) {
    console.error("Money execute error:", err);
    message.error({ content: "Xatolik: Kassa amalini saqlab bo'lmadi", key: "actionExec", duration: 3 });
    return false;
  }
}
